import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { startActivityTracking, stopActivityTracking, resetActivityTracking } from '../utils/activityTracker';
import { canAccessAdminPath, getRoleHomePath, formatRoleLabel } from '../utils/adminPermissions';
import { getAccessToken, getStoredUser, hydrateSessionFromRefresh, logoutSession } from '../utils/authSession';

function getPageTitle(pathname) {
  const titles = {
    '/admin/dashboard': 'Dashboard',
    '/admin/users': 'Users',
    '/admin/news': 'News & Posts',
    '/admin/blogs': 'Blogs',
    '/admin/events': 'Events',
    '/admin/gallery': 'Gallery',
    '/admin/projects': 'Projects',
    '/admin/announcements': 'Announcements & Site Theme',
    '/admin/diseases': 'Diseases & Conditions',
    '/admin/tests-procedures': 'Tests & Procedures',
    '/admin/symptom-browse': 'Symptom Browse Text',
    '/admin/services': 'Services',
    '/admin/departments': 'Departments',
    '/admin/senior-management': 'Senior Management',
    '/admin/donors-partners': 'Donors & Partners',
    '/admin/security': 'Security Monitor',
    '/admin/backups': 'System Backups',
    '/admin/activity-logs': 'Activity Logs',
    '/admin/change-password': 'Change Password'
  };

  const match = Object.keys(titles).find(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
  return titles[match] || 'Administration';
}

function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());
  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    stopActivityTracking();
    await logoutSession({ redirect: false });
    navigate('/admin/login');
  };

  useEffect(() => {
    let isCancelled = false;

    const ensureSessionAndAuthorize = async () => {
      let token = getAccessToken();
      if (!token) {
        const hydrated = await hydrateSessionFromRefresh();
        if (!hydrated || isCancelled) {
          if (!isCancelled) navigate('/admin/login');
          return;
        }
        token = getAccessToken();
      }

      let role = null;
      try {
        role = JSON.parse(localStorage.getItem('user') || '{}')?.role || null;
      } catch (_e) {
        role = null;
      }

      if (!token) {
        navigate('/admin/login');
        return;
      }

      if (!canAccessAdminPath(role, location.pathname)) {
        navigate(getRoleHomePath(role));
        return;
      }

      setUser(getStoredUser());
      resetActivityTracking();
      startActivityTracking(handleLogout);
    };

    ensureSessionAndAuthorize();

    return () => {
      isCancelled = true;
      stopActivityTracking();
    };
  }, [navigate, location.pathname]);

  const displayName = user?.full_name || user?.username || 'Administrator';
  const roleLabel = formatRoleLabel(user?.role);
  const initials = String(displayName)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'A';

  return (
    <div className={`admin-layout${sidebarOpen ? ' admin-sidebar-open' : ''}`}>
      <header className="admin-mobile-topbar">
        <button
          type="button"
          className="admin-mobile-topbar-btn"
          aria-label={sidebarOpen ? 'Close admin menu' : 'Open admin menu'}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen((open) => !open)}
        >
          <i className={`bi ${sidebarOpen ? 'bi-x-lg' : 'bi-list'}`} aria-hidden="true" />
          <span>{sidebarOpen ? 'Close' : 'Menu'}</span>
        </button>

        <div className="admin-mobile-topbar-title">
          <strong>{pageTitle}</strong>
          <small>{displayName}</small>
        </div>

        <button
          type="button"
          className="admin-mobile-topbar-btn admin-mobile-logout"
          aria-label="Sign out"
          onClick={handleLogout}
        >
          <i className="bi bi-box-arrow-right" aria-hidden="true" />
          <span>Logout</span>
        </button>
      </header>

      {sidebarOpen && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <AdminSidebar
        onLogout={handleLogout}
        show={sidebarOpen}
        onNavigate={() => setSidebarOpen(false)}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="admin-content">
        <header className="admin-topbar">
          <div className="admin-topbar-left">
            <p className="admin-topbar-eyebrow mb-0">Administration</p>
            <h1 className="admin-topbar-title mb-0">{pageTitle}</h1>
          </div>

          {user && (
            <div className="admin-topbar-right">
              <div className="admin-user-chip">
                <span className="admin-user-avatar" aria-hidden="true">
                  {initials}
                </span>
                <div className="admin-user-meta">
                  <strong>{displayName}</strong>
                  <span>{roleLabel}</span>
                </div>
              </div>
              <button
                type="button"
                className="admin-topbar-logout"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right" aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </header>

        <div className="admin-content-inner">{children}</div>
      </div>
    </div>
  );
}

export default AdminLayout;
